import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { TReviewFilterMode } from '@onesource/schemas';
import { forwardRef, type MouseEventHandler, type ReactNode } from 'react';
import { MdKeyboardArrowDown } from 'react-icons/md';

import { FILTER_ROSE_BG, FILTER_ROSE_DARK } from '../feed-filter-bar/filter-trigger-button';

/** Grey-blue for "everything except": clearly a different meaning from the rose "only". */
const EXCLUDE_BG = '#eef0f4';
const EXCLUDE_TEXT = '#3d4a63';

interface ReviewFilterTriggerProps {
  label: string;
  icon: ReactNode;
  /** Labels of the picked values; empty when the filter is off. */
  selectedLabels: string[];
  mode: TReviewFilterMode;
  open: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
}

/**
 * A filter button in the feed filter style. Off: icon + name. On: the pick inline
 * ("Module: Week 2", "Form: 3 selected"); an exclude pick reads "Module: not Week 2"
 * on a cool grey, so "only these" and "all but these" never look alike.
 */
export const ReviewFilterTrigger = forwardRef<HTMLButtonElement, ReviewFilterTriggerProps>(
  ({ label, icon, selectedLabels, mode, open, onClick }, ref) => {
    const isActive = selectedLabels.length > 0;
    const isExclude = isActive && mode === 'exclude';
    const summary = selectedLabels.length === 1 ? selectedLabels[0] : `${selectedLabels.length} selected`;

    return (
      <ButtonBase
        ref={ref}
        onClick={onClick}
        aria-haspopup="dialog"
        aria-expanded={open}
        // The pick can be cut with an ellipsis; the tooltip keeps the full text reachable.
        title={isActive ? `${label}: ${isExclude ? 'not ' : ''}${selectedLabels.join(', ')}` : undefined}
        sx={{
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          // A thumb-sized target on phones, the compact feed size on desktop.
          height: { xs: 40, md: 34 },
          maxWidth: 280,
          px: '12px',
          borderRadius: '10px',
          fontFamily: 'Figtree, sans-serif',
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: isExclude ? EXCLUDE_TEXT : isActive ? FILTER_ROSE_DARK : '#666',
          bgcolor: isExclude ? EXCLUDE_BG : isActive ? FILTER_ROSE_BG : open ? '#F4F4F4' : 'transparent',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '&:hover': { bgcolor: isExclude ? '#e4e7ee' : isActive ? '#f6e4e8' : '#F4F4F4' },
          '&.Mui-focusVisible': { outline: '2px solid', outlineColor: FILTER_ROSE_DARK, outlineOffset: 1 },
        }}
      >
        <Box component="span" sx={{ display: 'inline-flex', fontSize: 16 }}>
          {icon}
        </Box>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {label}
          {isActive && (
            <>
              :&nbsp;
              <Box component="span" sx={{ fontWeight: 700 }}>
                {isExclude ? `not ${summary}` : summary}
              </Box>
            </>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            transition: 'transform 0.15s ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          <MdKeyboardArrowDown size={16} />
        </Box>
      </ButtonBase>
    );
  },
);
ReviewFilterTrigger.displayName = 'ReviewFilterTrigger';
